import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Check, Copy, Link2, Share2, Trash2 } from "lucide-react";
import {
  CreatedInvitation,
  InvitationSummary,
  InvitationType,
  createInvitation,
  listInvitations,
  revokeInvitation,
} from "../../utils/api";

export interface InviteOption {
  type: InvitationType;
  targetId: string;
  label: string;
  /** Lets the creator choose viewer/editor. */
  permission?: boolean;
  /** Lets the creator choose the shared activities (dog → trainer). */
  activities?: boolean;
}

interface InviteLinkDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  title: string;
  description?: string;
  options: InviteOption[];
}

const ACTIVITIES = [
  { value: "mantrailing", label: "Mantrailing" },
  { value: "hiking", label: "Randonnée" },
  { value: "canicross", label: "Canicross" },
  { value: "detection", label: "Détection" },
];
const LIFETIMES = [
  { hours: 1, label: "1 heure" },
  { hours: 24, label: "24 heures" },
  { hours: 24 * 7, label: "7 jours" },
  { hours: 24 * 30, label: "30 jours" },
];

const selectClass = "h-9 w-full rounded-md border bg-background px-2 text-sm";

export const InviteLinkDialog: React.FC<InviteLinkDialogProps> = ({ isOpen, onOpenChange, title, description, options }) => {
  const [optionIndex, setOptionIndex] = useState(0);
  const [hours, setHours] = useState(24);
  const [maxUses, setMaxUses] = useState(1);
  const [permission, setPermission] = useState<"viewer" | "editor">("viewer");
  const [activities, setActivities] = useState<string[]>(["mantrailing"]);
  const [created, setCreated] = useState<CreatedInvitation | null>(null);
  const [existing, setExisting] = useState<InvitationSummary[]>([]);
  const [copied, setCopied] = useState<"code" | "link" | null>(null);

  const option = options[Math.min(optionIndex, options.length - 1)];
  const link = created ? `${window.location.origin}${created.path}` : "";

  const refresh = useCallback(async () => {
    if (!option) return;
    try {
      setExisting((await listInvitations(option.type, option.targetId)).filter(i => i.active));
    } catch {
      setExisting([]);
    }
  }, [option]);

  useEffect(() => {
    if (isOpen) {
      setCreated(null);
      refresh();
    }
  }, [isOpen, optionIndex, refresh]);

  const handleCreate = async () => {
    if (option.activities && activities.length === 0) {
      toast.error("Choisissez au moins une activité.");
      return;
    }
    try {
      setCreated(await createInvitation({
        type: option.type,
        targetId: option.targetId,
        expiresInHours: hours,
        maxUses,
        permission: option.permission ? permission : undefined,
        activities: option.activities ? activities : undefined,
      }));
      refresh();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Impossible de créer l'invitation.");
    }
  };

  const copy = async (kind: "code" | "link") => {
    await navigator.clipboard.writeText(kind === "code" ? created!.code : link);
    setCopied(kind);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleRevoke = async (id: string) => {
    await revokeInvitation(id).catch(() => toast.error("Révocation impossible."));
    refresh();
  };

  const canShare = typeof navigator !== "undefined" && "share" in navigator;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description || "Partagez un lien ou un code. Vous pouvez limiter la durée et le nombre d'utilisations."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {options.length > 1 && (
            <div className="space-y-1">
              <Label htmlFor="invite-kind">Inviter</Label>
              <select id="invite-kind" className={selectClass} value={optionIndex} onChange={e => setOptionIndex(Number(e.target.value))}>
                {options.map((o, i) => <option key={o.type + i} value={i}>{o.label}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="invite-hours">Valable</Label>
              <select id="invite-hours" className={selectClass} value={hours} onChange={e => setHours(Number(e.target.value))}>
                {LIFETIMES.map(l => <option key={l.hours} value={l.hours}>{l.label}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-uses">Utilisations max</Label>
              <Input id="invite-uses" type="number" min={1} max={100} value={maxUses} onChange={e => setMaxUses(Math.max(1, Math.min(100, Number(e.target.value) || 1)))} />
            </div>
          </div>

          {option.permission && (
            <div className="space-y-1">
              <Label htmlFor="invite-permission">Droits</Label>
              <select id="invite-permission" className={selectClass} value={permission} onChange={e => setPermission(e.target.value as "viewer" | "editor")}>
                <option value="viewer">Lecteur (consulte seulement)</option>
                <option value="editor">Éditeur (planifie et modifie)</option>
              </select>
            </div>
          )}

          {option.activities && (
            <div className="space-y-1">
              <Label>Activités partagées</Label>
              <div className="flex flex-wrap gap-2">
                {ACTIVITIES.map(a => (
                  <label key={a.value} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={activities.includes(a.value)}
                      onChange={e => setActivities(prev => e.target.checked ? [...prev, a.value] : prev.filter(v => v !== a.value))}
                    />
                    {a.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <Button className="w-full" onClick={handleCreate}><Link2 className="mr-2 h-4 w-4" />Créer le lien d'invitation</Button>

          {created && (
            <div className="space-y-2 rounded-md border bg-muted/40 p-3">
              <div className="space-y-1">
                <Label>Lien</Label>
                <div className="flex gap-2">
                  <Input value={link} readOnly onFocus={e => e.target.select()} />
                  <Button variant="secondary" size="icon" onClick={() => copy("link")} aria-label="Copier le lien">
                    {copied === "link" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  {canShare && (
                    <Button variant="secondary" size="icon" aria-label="Partager" onClick={() => navigator.share({ title, url: link }).catch(() => undefined)}>
                      <Share2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                <Label>Ou code à saisir</Label>
                <div className="flex gap-2">
                  <Input value={created.code} readOnly className="font-mono tracking-widest" onFocus={e => e.target.select()} />
                  <Button variant="secondary" size="icon" onClick={() => copy("code")} aria-label="Copier le code">
                    {copied === "code" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Expire le {new Date(created.expiresAt).toLocaleString("fr-FR")} · {created.maxUses} utilisation(s). Le code n'est affiché qu'une fois.
              </p>
            </div>
          )}

          {existing.length > 0 && (
            <div className="space-y-1">
              <Label>Invitations actives</Label>
              {existing.map(inv => (
                <div key={inv._id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                  <span>
                    {inv.usedCount}/{inv.maxUses} utilisé(s) <Badge variant="outline" className="ml-2">jusqu'au {new Date(inv.expiresAt).toLocaleDateString("fr-FR")}</Badge>
                  </span>
                  <Button variant="ghost" size="icon" aria-label="Révoquer" onClick={() => handleRevoke(inv._id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
